import { defaultDateDef, simpleNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { DateCellEditor } from '@components/_SiteWide/Grid/CellEditors/DateCellEditor'
import { Status } from '@components/_SiteWide/Grid/CellRenderers/CardingCredentialsStatus'
import { StringComparator } from '@components/_SiteWide/Grid/Comparators'
import { CustomHeaderTooltips } from '@components/_SiteWide/Grid/CustomHeaderTooltips'
import { addCommasToNumber, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import type { ColDef } from 'ag-grid-community'
import { Button, Tooltip } from 'antd'
import React from 'react'

export const getColumnDefs = (handleCredentialLink): ColDef[] => {
  return [
    { headerName: 'Depot', field: 'depot', editable: false, rowGroupIndex: 1, initialHide: true },
    { headerName: 'Driver', field: 'driver', editable: false, rowGroupIndex: 2, initialHide: true },
    {
      headerName: 'Credential Name',
      field: 'credential_name',
      editable: false,
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => <CustomHeaderTooltips displayValue={() => <Texto>List Driver Credentials</Texto>} />}
        />
      ),
      cellRenderer: (params) => {
        return (
          <Button
            type='link'
            style={{ textAlign: 'left' }}
            onClick={() => handleCredentialLink(params.data.credential_id)}
          >
            {params.value}
          </Button>
        )
      },
      comparator: StringComparator,
    },
    {
      headerName: 'Status',
      field: 'status',
      valueGetter: (params) => params.data?.status,
      aggFunc: (props) => {
        return props.values
      },
      cellRenderer: Status,
      initialWidth: 120,
      editable: false,
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => (
            <>
              <Texto>
                Valid - Certification date and days valid are populated, and it is not within warning threshold.
              </Texto>
              <Texto>Expires Soon - Expiration is within warning threshold.</Texto>
              <Texto>Expired - Expiration is beyond certification date</Texto>
              <Texto>
                Missing Date - No certification date and/or days valid and Dispatch Impact is not set to Informational
                Will still alert dispatch
              </Texto>
              <Texto>NA - No certification date and/or days valid and Dispatch Impact is set to Informational.</Texto>
            </>
          )}
        />
      ),
    },
    {
      headerName: 'Certification Date',
      field: 'certification_date',
      editable: true,
      cellEditor: DateCellEditor,
      cellEditorParams: (params) => ({
        accessor: 'certification_date',
        disabledDate: (current) => current && current > new Date(),
      }),
      ...defaultDateDef('certification_date', formatters.DATE),
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => (
            <>
              <Texto>Enter the date the Driver was last certified or last performed the certified activity.</Texto>
              <Texto>
                Credentials with no certification date and/or days valid and Dispatch Impact is not set to Informational
                (No Impact) will alert dispatch.
              </Texto>
              <Texto> Credentials with Dispatch Impact set to Informational (No Impact) will not alert dispatch.</Texto>
            </>
          )}
        />
      ),
    },
    {
      headerName: 'Expiration Date',
      field: 'expiration_date',
      editable: false,
      ...defaultDateDef('expiration_date', formatters.DATE),
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => (
            <>
              <Texto>Auto updates based on Certification Date and Days Valid.</Texto>
              <Texto>
                Credentials with no expiration date and/or days valid and Dispatch Impact is not set to Informational
                (No Impact) will alert dispatch.
              </Texto>
              <Texto> Credentials with Dispatch Impact set to Informational (No Impact) will not alert dispatch.</Texto>
            </>
          )}
        />
      ),
      cellRenderer: (params) => {
        if (params.node.group) return null
        const lastUpdated = params.data?.last_updated

        const title =
          lastUpdated && lastUpdated?.updated_by && lastUpdated?.updated_on
            ? `${lastUpdated?.updated_by} last updated ${formatters.DATE_TIME(lastUpdated?.updated_on)}`
            : ''
        return (
          <Tooltip title={title} trigger='hover'>
            <GraviButton
              buttonText={formatters.DATE(params.data?.expiration_date)}
              className='ghost-gravi-button'
              style={{ fontWeight: 'normal' }}
            />
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Days Valid',
      field: 'days_valid',
      editable: false,
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips
          displayValue={() => <Texto>Days after driver certification date before expiration</Texto>}
        />
      ),
      ...simpleNumberDef('days_valid', 0),
      valueGetter: (params) => {
        if (params.node.group || params.data.dispatch_impact === 'Informational (No Impact)') return null
        if (params.data.days_valid || params.data.days_valid === 0) return addCommasToNumber(params.data.days_valid, 0)
      },
    },
    {
      headerName: 'Warning Threshold',
      field: 'warning_threshold',
      editable: false,
      ...simpleNumberDef('warning_threshold', 0),
      headerTooltip: 'true',
      tooltipComponent: (params) => (
        <CustomHeaderTooltips displayValue={() => <Texto>Days before expiration to trigger a warning</Texto>} />
      ),
      valueGetter: (params) => {
        if (params.node.group || params.data.dispatch_impact === 'Informational (No Impact)') return null

        if (params?.data?.warning_threshold || params?.data?.warning_threshold === 0)
          return addCommasToNumber(params?.data?.warning_threshold, 0)
      },
    },
    {
      headerName: 'Dispatch Impact',
      field: 'dispatch_impact',
      minWidth: 200,
      tooltipComponent: (params) => {
        const items = [
          {
            label: 'Alert',
            description: 'Alert on Driver Dispatch Driver Card and can dispatch',
          },
          {
            label: 'Alert Manual, Block Optimizer',
            description: `Alert on Driver Dispatch Driver Card of manually dispatched orders but blocks auto dispatching by Route Optimizer.  Missing card or certification date will alert dispatch.`,
          },
          {
            label: 'Block',
            description: 'Blocks dispatch with error message',
          },
          {
            label: 'Informational (No Impact)',
            description: 'No alert or blocking dispatch',
          },
        ]
        return (
          <CustomHeaderTooltips
            displayValue={() => (
              <>
                <Texto className='mb-1'>
                  If orders or backhauls are attempted to be dispatched to a driver with no or an expired card or
                  credential then:
                </Texto>
                {items.map((item) => (
                  <Horizontal key={item.label} className='mb-1'>
                    <Texto style={{ whiteSpace: 'nowrap' }} className='mr-2'>
                      {item.label}:
                    </Texto>
                    <Texto>{item.description}</Texto>
                  </Horizontal>
                ))}
              </>
            )}
          />
        )
      },
      tooltipComponentParams: { width: `600px` },
      headerTooltip: 'true',
      cellStyle: (params) => {
        const dispatchImpact = params.data?.dispatch_impact
        const bgColor =
          dispatchImpact === 'Block Dispatch'
            ? 'var(--theme-error-dim)'
            : dispatchImpact === 'Informational (No Impact)'
              ? 'var(--theme-color-2-dim)'
              : dispatchImpact?.includes('Alert')
                ? 'var(--theme-warning-dim)'
                : ''
        return { backgroundColor: bgColor }
      },
      editable: false,
    },
    { headerName: 'Market', field: 'market', editable: false, initialHide: true },
  ]
}
